import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { type OperatingMarketView, type PricingRuleView, approvePricingRule, createPricingRule, listPricingRules } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { type FormatContext, formatDistance, formatMoney } from '@vello/format';
import {
  maySelfApprove,
  type TariffForm,
  type TariffFormField,
  type TariffFormIssue,
  effectiveTariff,
  emptyBandRow,
  emptyPairRow,
  emptyTariffForm,
  MAX_TARIFF_BANDS,
  MAX_TARIFF_PAIRS,
  formatMarketInstant,
  marketDisplayName,
  sameRegion,
  marketTimeZone,
  normalizeTariffForm,
  pricingVersionNumber,
  tariffApprovalView,
  tariffFormFromRule,
  tariffHistory,
  tariffStatusTone,
  utcToZonedLocal,
} from '../../src/index';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, EmptyState, Field, ListTable, MoneyInput, Notice, PageHeader, Panel, Select, TextInput, FormRow, SectionTitle, FormGrid, Cluster, Grid, Stack, Text } from '@vello/web-theme';
import { PersonName } from '../components/person-name';
import { LoadingBlock } from '../components/loading-block';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { DetailRow as Row } from '../components/detail-row';

interface Loaded {
  readonly marketId: string;
  readonly categoryCode: string;
  readonly rules: readonly PricingRuleView[];
}

/** The registered markets the operator's scope reaches (global → all; country → its markets; city → that market). */
function marketsInScope(allowed: readonly { country?: string; region?: string; city?: string }[], markets: readonly OperatingMarketView[]): readonly OperatingMarketView[] {
  const global = allowed.some((s) => s.country === undefined && s.city === undefined);
  return markets
    .filter((m) => m.status !== 'RETIRED')
    .filter((m) => global || allowed.some((s) => s.city === m.id || (s.city === undefined && s.region !== undefined && sameRegion(s.region, m.regionCode)) || (s.city === undefined && s.region === undefined && s.country !== undefined && s.country === m.countryCode.toUpperCase())))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Seconds as the largest whole unit (hour / minute / second), localized. */
function perTime(seconds: number, locale: string): string {
  const [value, unit] = seconds % 3600 === 0 ? [seconds / 3600, 'hour'] : seconds % 60 === 0 ? [seconds / 60, 'minute'] : [seconds, 'second'];
  return new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'long' }).format(value);
}

/**
 * **Tarifas por categoria** (W1-ADMIN, D358; deferred at D330) over the governed pricing-rule routes: pick a market (the
 * registered catalog inside the operator's scope, value `cc:slug`) and a category, read the version history and the
 * version in force, register a new version (the server creates and submits it for approval), and approve a pending one.
 *
 * FOUR EYES. Approval is a second operator's act; the server refuses the maker (403) and that refusal is shown. The screen
 * tells a maker up front, but never hides the action (the server is the gate). Amounts are typed in currency units through
 * `MoneyInput` (never cents); the effective window is typed in the market's wall clock and sent as UTC; every instant
 * renders in the market timezone. The single primary action is "Submit for approval".
 */
export function TariffsScreen(): ReactElement {
  const { runtime, scope, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { confirm, toast } = useConsole();
  const { t, lang } = useT();
  const { v, fail } = useVocab();

  const options = useMemo(() => marketsInScope(scope.allowed, markets), [scope.allowed, markets]);
  // The market is the header scope's (owner, 2026-10-12): the scoped city, or the only market the grants reach.
  const { market: scopeMarket, choices: marketChoices } = useScopeMarket();
  const marketId = scopeMarket ?? '';
  const [category, setCategory] = useState('');
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [form, setForm] = useState<TariffForm>(() => emptyTariffForm());
  const [issues, setIssues] = useState<readonly TariffFormIssue[]>([]);
  const [busy, setBusy] = useState(false);

  // Switching the scope market drops the loaded history and its form: a draft never carries over to another market.
  useEffect(() => { setLoaded(undefined); setError(undefined); setIssues([]); }, [marketId]);

  const tz = marketTimeZone(loaded?.marketId, markets);
  const fmtCtx: FormatContext = { locale: lang, timeZone: tz ?? 'UTC', measurement: 'metric' };
  const money = (minor: string, currency: string): string => (/^\d+$/.test(minor) && isIso4217Currency(currency) ? formatMoney({ amountMinor: BigInt(minor), currency }, fmtCtx) : '—');
  const when = (instant: string | undefined): string => formatMarketInstant(instant, tz, lang);
  const issueOf = (field: TariffFormField): string | undefined => {
    const i = issues.find((x) => x.field === field);
    return i !== undefined ? t(`tariff.issue.${i.code}`) : undefined;
  };
  const set = <K extends keyof TariffForm>(key: K) => (value: TariffForm[K]): void => setForm((f) => ({ ...f, [key]: value }));

  const load = async (m: string, c: string, keepForm = false): Promise<void> => {
    if (m === '' || c.trim() === '' || loading) return;
    setLoading(true);
    setError(undefined);
    const res = await listPricingRules(runtime, { market: m, category: c.trim() });
    setLoading(false);
    if (!res.ok) {
      setLoaded(undefined);
      setError(t('tariff.error', { code: fail(res.error) }));
      return;
    }
    const next: Loaded = { marketId: m, categoryCode: c.trim(), rules: tariffHistory(res.value.rules) };
    setLoaded(next);
    if (!keepForm) {
      const zone = marketTimeZone(m, markets);
      const latest = next.rules[0];
      setForm({ ...emptyTariffForm(m, next.categoryCode, latest?.currency ?? ''), effectiveFromLocal: zone !== undefined ? (utcToZonedLocal(new Date().toISOString(), zone) ?? '') : '' });
      setIssues([]);
    }
  };

  const approve = async (rule: PricingRuleView): Promise<void> => {
    if (busy || loaded === undefined) return;
    const version = String(pricingVersionNumber(rule.pricingVersion));
    const answer = await confirm({
      title: t('tariff.approve.title', { v: version }),
      body: t('tariff.approve.body', { market: marketDisplayName(rule.marketId, markets), category: v('ride.category', rule.categoryCode) }),
      confirmLabel: t('tariff.approve.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: 'primary',
      icon: 'badgeCheck',
      reasonLabel: t('tariff.approve.reasonLabel'),
      notePlaceholder: t('tariff.approve.note'),
      requireNote: true,
    });
    const reason = answer.note?.trim() ?? '';
    if (!answer.confirmed || reason === '') return;
    setBusy(true);
    const res = await approvePricingRule(runtime, rule.pricingVersion, { approvalReason: reason });
    setBusy(false);
    if (res.ok) {
      toast({ message: t('tariff.approve.toast', { v: version }), tone: 'success' });
      await load(loaded.marketId, loaded.categoryCode, true);
    } else {
      toast({ message: res.error.status === 403 ? t('tariff.approve.forbidden') : t('tariff.saveError', { code: fail(res.error) }), tone: 'warning' });
    }
  };

  const submit = async (): Promise<void> => {
    if (busy || loaded === undefined) return;
    const normalized = normalizeTariffForm({ ...form, marketId: loaded.marketId, categoryCode: loaded.categoryCode }, tz);
    if (!normalized.ok) {
      setIssues(normalized.error);
      return;
    }
    setIssues([]);
    setBusy(true);
    const res = await createPricingRule(runtime, normalized.value);
    setBusy(false);
    if (res.ok) {
      toast({ message: t('tariff.created.toast', { v: String(pricingVersionNumber(res.value.rule.pricingVersion)) }), tone: 'success' });
      await load(loaded.marketId, loaded.categoryCode);
    } else {
      toast({ message: t('tariff.saveError', { code: fail(res.error) }), tone: 'warning' });
    }
  };

  const inForce = loaded !== undefined ? effectiveTariff(loaded.rules, new Date().toISOString()) : undefined;
  const currencyOk = isIso4217Currency(form.currency.trim().toUpperCase()) && /^[A-Za-z]{3}$/.test(form.currency.trim());
  const currency = form.currency.trim().toUpperCase();
  const moneyIssue = (issue: string): string => t(`money.issue.${issue}`);

  return (
    <>
      <PageHeader title={t('tariff.title')} subtitle={t('tariff.subtitle')} />
      <Notice tone="neutral">{t('tariff.fourEyes')}</Notice>

      {marketId === '' ? (
        <Panel><ScopeMarketNotice choices={marketChoices} /></Panel>
      ) : (
        <Panel>
          <FormRow
            columns="minmax(0, 1fr)"
            action={
              <Button variant="secondary" onClick={() => void load(marketId, category)} disabled={loading || category.trim() === ''}>{t('ui.load')}</Button>
            }
          >
            <Field label={t('tariff.filter.category')} htmlFor="tariff-category" hint={t('tariff.filter.category.hint')}>
              <TextInput id="tariff-category" value={category} onChange={(e) => setCategory(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void load(marketId, category); }} autoComplete="off" spellCheck={false} />
            </Field>
          </FormRow>
        </Panel>
      )}

      {error !== undefined && <Notice tone="danger">{error}</Notice>}

      {loading ? (
        <Panel><LoadingBlock /></Panel>
      ) : loaded === undefined ? (
        marketId === '' ? null : <Panel><EmptyState title={t('tariff.prompt')} detail={t('tariff.prompt.detail')} /></Panel>
      ) : (
        <>
          {tz === undefined && <Notice tone="warning">{t('tariff.noTimezone')}</Notice>}

          <Panel>
            <Stack gap={3}>
            <SectionTitle>{t('tariff.effective.title')}</SectionTitle>
            {inForce === undefined ? (
              <Notice tone="warning">{t('tariff.effective.none')}</Notice>
            ) : (
              <Grid min="md" gap={6}>
                <Row label={t('tariff.col.version')} value={`v${pricingVersionNumber(inForce.pricingVersion)}`} />
                <Row label={t('tariff.col.effective')} value={t('tariff.window', { from: when(inForce.effectiveFrom), to: inForce.effectiveTo !== undefined ? when(inForce.effectiveTo) : t('tariff.window.open') })} />
                <Row label={t('tariff.col.baseFare')} value={money(inForce.baseFareMinor, inForce.currency)} />
                <Row label={t('tariff.col.minimumFare')} value={money(inForce.minimumFareMinor, inForce.currency)} />
                <Row label={t('tariff.detail.distanceRate')} value={t('tariff.rate', { amount: money(inForce.distanceRate.minorUnits, inForce.currency), unit: formatDistance(inForce.distanceRate.perUnit, fmtCtx, 3) })} />
                <Row label={t('tariff.detail.timeRate')} value={t('tariff.rate', { amount: money(inForce.timeRate.minorUnits, inForce.currency), unit: perTime(inForce.timeRate.perUnit, lang) })} />
                <Row label={t('tariff.detail.ttl')} value={perTime(inForce.quoteTtlSeconds, lang)} />
                {inForce.perStopFeeMinor !== undefined && <Row label={t('tariff.field.perStop')} value={money(inForce.perStopFeeMinor, inForce.currency)} />}
                {inForce.bookingFeeMinor !== undefined && <Row label={t('tariff.field.bookingFee')} value={money(inForce.bookingFeeMinor, inForce.currency)} />}
                {inForce.percentFeeBasisPoints !== undefined && <Row label={t('tariff.field.percentFee')} value={`${new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(inForce.percentFeeBasisPoints / 100)} %`} />}
                {inForce.waitRate !== undefined && (
                  <Row label={t('tariff.detail.wait')} value={t('tariff.detail.waitValue', { rate: money(inForce.waitRate.minorUnits, inForce.currency), free: perTime(inForce.freeWaitSeconds ?? 0, lang), penalty: money(inForce.waitPenaltyMinor ?? '0', inForce.currency), max: perTime(inForce.maxWaitSeconds ?? 0, lang) })} />
                )}
                {inForce.distanceBands !== undefined && <Row label={t('tariff.bands.title')} value={t('tariff.detail.count', { n: String(inForce.distanceBands.length) })} />}
                {inForce.intercityPairs !== undefined && <Row label={t('tariff.pairs.title')} value={t('tariff.detail.count', { n: String(inForce.intercityPairs.length) })} />}
                <Row label={t('tariff.col.approver')} mono={false}><PersonName id={inForce.approvedBy} /><SelfApprovalNote record={inForce} /></Row>
              </Grid>
            )}
            </Stack>
          </Panel>

          <Stack gap={3} as="section">
            <SectionTitle>{t('tariff.history.title')}</SectionTitle>
            <ListTable
              columns={[
                { key: 'version', header: t('tariff.col.version'), width: '.6fr', render: (r: PricingRuleView) => <span className="tnum">v{pricingVersionNumber(r.pricingVersion)}</span> },
                {
                  key: 'status',
                  header: t('tariff.col.status'),
                  width: '1.3fr',
                  render: (r: PricingRuleView) => {
                    const view = tariffApprovalView(r, me?.id, selfApproval);
                    return (
                      <Stack gap={1} align="start">
                        <Badge tone={tariffStatusTone(r.status)}>{v('pricing_governance.rule_status', r.status)}</Badge>
                        {view.pending && view.viewerIsMaker && <Text role="caption" tone="tertiary">{t('tariff.approve.makerNote')}</Text>}
                      </Stack>
                    );
                  },
                },
                { key: 'effective', header: t('tariff.col.effective'), width: '1.8fr', render: (r: PricingRuleView) => <span>{t('tariff.window', { from: when(r.effectiveFrom), to: r.effectiveTo !== undefined ? when(r.effectiveTo) : t('tariff.window.open') })}</span> },
                { key: 'base', header: t('tariff.col.baseFare'), numeric: true, render: (r: PricingRuleView) => money(r.baseFareMinor, r.currency) },
                { key: 'min', header: t('tariff.col.minimumFare'), numeric: true, render: (r: PricingRuleView) => money(r.minimumFareMinor, r.currency) },
                { key: 'maker', header: t('tariff.col.maker'), width: '1fr', render: (r: PricingRuleView) => <Text role="caption">{r.maker}</Text> },
                { key: 'approver', header: t('tariff.col.approver'), width: '1fr', render: (r: PricingRuleView) => <Text role="caption">{r.approvedBy !== undefined ? <><PersonName id={r.approvedBy} /> · {when(r.approvedAt)}</> : '—'}</Text> },
              ]}
              rows={loaded.rules}
              rowKey={(r) => r.pricingVersion}
              actionsWidth="max-content"
              actions={(r) => (
                <>
                  {r.status === 'PENDING_APPROVAL' && <Button size="sm" variant="secondary" onClick={() => void approve(r)} disabled={busy}>{t('tariff.approve.action')}</Button>}
                  <Button size="sm" variant="ghost" onClick={() => { setForm({ ...tariffFormFromRule(r), effectiveFromLocal: form.effectiveFromLocal }); setIssues([]); }} disabled={busy}>{t('tariff.revise.action')}</Button>
                </>
              )}
              empty={<EmptyState title={t('tariff.history.empty')} />}
            />
          </Stack>

          <Panel>
            <Stack gap={5}>
            <Stack gap={1}>
              <SectionTitle>{t('tariff.form.title')}</SectionTitle>
              <Text as="p" role="caption" tone="secondary">
                {marketDisplayName(loaded.marketId, markets)} · {v('ride.category', loaded.categoryCode)} — {t('tariff.form.subtitle')}
              </Text>
            </Stack>
            <FormGrid>
              <Field label={t('tariff.field.currency')} htmlFor="tariff-currency" {...(issueOf('currency') !== undefined ? { hint: issueOf('currency') } : {})}>
                <TextInput id="tariff-currency" className="tnum" value={form.currency} maxLength={3} onChange={(e) => set('currency')(e.target.value.toUpperCase())} aria-invalid={issueOf('currency') !== undefined} autoComplete="off" spellCheck={false} />
              </Field>
              <MoneyInput id="tariff-base" label={t('tariff.field.baseFare')} currency={currency} locale={lang} valueMinor={form.baseFareMinor} onChangeMinor={set('baseFareMinor')} issueText={moneyIssue} required disabled={!currencyOk} {...(issueOf('baseFareMinor') !== undefined ? { hint: issueOf('baseFareMinor') } : {})} />
              <MoneyInput id="tariff-minimum" label={t('tariff.field.minimumFare')} currency={currency} locale={lang} valueMinor={form.minimumFareMinor} onChangeMinor={set('minimumFareMinor')} issueText={moneyIssue} required disabled={!currencyOk} {...(issueOf('minimumFareMinor') !== undefined ? { hint: issueOf('minimumFareMinor') } : {})} />
              <MoneyInput id="tariff-distance-rate" label={t('tariff.field.distanceRate')} currency={currency} locale={lang} valueMinor={form.distanceRateMinor} onChangeMinor={set('distanceRateMinor')} issueText={moneyIssue} required disabled={!currencyOk} {...(issueOf('distanceRateMinor') !== undefined ? { hint: issueOf('distanceRateMinor') } : {})} />
              <Field label={t('tariff.field.distancePer')} htmlFor="tariff-distance-per" {...(issueOf('distancePerMeters') !== undefined ? { hint: issueOf('distancePerMeters') } : {})}>
                <TextInput id="tariff-distance-per" className="tnum" inputMode="numeric" value={form.distancePerMeters} onChange={(e) => set('distancePerMeters')(e.target.value)} aria-invalid={issueOf('distancePerMeters') !== undefined} />
              </Field>
              <MoneyInput id="tariff-time-rate" label={t('tariff.field.timeRate')} currency={currency} locale={lang} valueMinor={form.timeRateMinor} onChangeMinor={set('timeRateMinor')} issueText={moneyIssue} required disabled={!currencyOk} {...(issueOf('timeRateMinor') !== undefined ? { hint: issueOf('timeRateMinor') } : {})} />
              <Field label={t('tariff.field.timePer')} htmlFor="tariff-time-per" {...(issueOf('timePerSeconds') !== undefined ? { hint: issueOf('timePerSeconds') } : {})}>
                <TextInput id="tariff-time-per" className="tnum" inputMode="numeric" value={form.timePerSeconds} onChange={(e) => set('timePerSeconds')(e.target.value)} aria-invalid={issueOf('timePerSeconds') !== undefined} />
              </Field>
              <Field label={t('tariff.field.ttl')} htmlFor="tariff-ttl" {...(issueOf('quoteTtlSeconds') !== undefined ? { hint: issueOf('quoteTtlSeconds') } : {})}>
                <TextInput id="tariff-ttl" className="tnum" inputMode="numeric" value={form.quoteTtlSeconds} onChange={(e) => set('quoteTtlSeconds')(e.target.value)} aria-invalid={issueOf('quoteTtlSeconds') !== undefined} />
              </Field>
              <Field label={t('tariff.field.effectiveFrom')} htmlFor="tariff-from" hint={issueOf('effectiveFromLocal') ?? t('tariff.field.localTime', { tz: tz ?? 'UTC' })}>
                <TextInput id="tariff-from" className="tnum" type="datetime-local" value={form.effectiveFromLocal} onChange={(e) => set('effectiveFromLocal')(e.target.value)} aria-invalid={issueOf('effectiveFromLocal') !== undefined} />
              </Field>
              <Field label={t('tariff.field.effectiveTo')} htmlFor="tariff-to" hint={issueOf('effectiveToLocal') ?? t('tariff.field.localTime', { tz: tz ?? 'UTC' })}>
                <TextInput id="tariff-to" className="tnum" type="datetime-local" value={form.effectiveToLocal} onChange={(e) => set('effectiveToLocal')(e.target.value)} aria-invalid={issueOf('effectiveToLocal') !== undefined} />
              </Field>
            </FormGrid>
            <Stack gap={3}>
            <Stack gap={1}>
              <SectionTitle level={3} size="sub">{t('tariff.components.title')}</SectionTitle>
              <Text as="p" role="caption" tone="secondary">{t('tariff.components.hint')}</Text>
            </Stack>
            <FormGrid>
              <MoneyInput id="tariff-per-stop" label={t('tariff.field.perStop')} currency={currency} locale={lang} valueMinor={form.perStopFeeMinor} onChangeMinor={set('perStopFeeMinor')} issueText={moneyIssue} disabled={!currencyOk} />
              <MoneyInput id="tariff-booking-fee" label={t('tariff.field.bookingFee')} currency={currency} locale={lang} valueMinor={form.bookingFeeMinor} onChangeMinor={set('bookingFeeMinor')} issueText={moneyIssue} disabled={!currencyOk} />
              <Field label={t('tariff.field.percentFee')} htmlFor="tariff-percent-fee" hint={issueOf('percentFeePercent') ?? t('tariff.field.percentFee.hint')}>
                <TextInput id="tariff-percent-fee" className="tnum" inputMode="decimal" value={form.percentFeePercent} onChange={(e) => set('percentFeePercent')(e.target.value)} aria-invalid={issueOf('percentFeePercent') !== undefined} />
              </Field>
              <MoneyInput id="tariff-wait-rate" label={t('tariff.field.waitRate')} currency={currency} locale={lang} valueMinor={form.waitRateMinor} onChangeMinor={set('waitRateMinor')} issueText={moneyIssue} disabled={!currencyOk} {...(issueOf('waitRateMinor') !== undefined ? { hint: issueOf('waitRateMinor') } : {})} />
              <Field label={t('tariff.field.freeWait')} htmlFor="tariff-free-wait" {...(issueOf('freeWaitMinutes') !== undefined ? { hint: issueOf('freeWaitMinutes') } : {})}>
                <TextInput id="tariff-free-wait" className="tnum" inputMode="numeric" value={form.freeWaitMinutes} onChange={(e) => set('freeWaitMinutes')(e.target.value)} aria-invalid={issueOf('freeWaitMinutes') !== undefined} />
              </Field>
              <MoneyInput id="tariff-wait-penalty" label={t('tariff.field.waitPenalty')} currency={currency} locale={lang} valueMinor={form.waitPenaltyMinor} onChangeMinor={set('waitPenaltyMinor')} issueText={moneyIssue} disabled={!currencyOk} />
              <Field label={t('tariff.field.maxWait')} htmlFor="tariff-max-wait" hint={issueOf('maxWaitMinutes') ?? t('tariff.field.maxWait.hint')}>
                <TextInput id="tariff-max-wait" className="tnum" inputMode="numeric" value={form.maxWaitMinutes} onChange={(e) => set('maxWaitMinutes')(e.target.value)} aria-invalid={issueOf('maxWaitMinutes') !== undefined} />
              </Field>
            </FormGrid>
            </Stack>

            <Stack gap={3}>
            <Stack gap={1}>
              <SectionTitle level={3} size="sub">{t('tariff.bands.title')}</SectionTitle>
              <Text as="p" role="caption" tone={issueOf('bands') !== undefined ? 'danger' : 'secondary'}>{issueOf('bands') ?? t('tariff.bands.hint')}</Text>
            </Stack>
            {form.bands.map((row, i) => (
              <FormRow key={`band-${i}`} action={<Button variant="ghost" onClick={() => set('bands')(form.bands.filter((_, j) => j !== i))}>{t('tariff.row.remove')}</Button>}>
                <Field label={t('tariff.bands.from')} htmlFor={`tariff-band-from-${i}`}>
                  <TextInput id={`tariff-band-from-${i}`} className="tnum" inputMode="decimal" value={row.fromKm} onChange={(e) => set('bands')(form.bands.map((b, j) => (j === i ? { ...b, fromKm: e.target.value } : b)))} />
                </Field>
                <Field label={t('tariff.bands.to')} htmlFor={`tariff-band-to-${i}`}>
                  <TextInput id={`tariff-band-to-${i}`} className="tnum" inputMode="decimal" value={row.toKm} onChange={(e) => set('bands')(form.bands.map((b, j) => (j === i ? { ...b, toKm: e.target.value } : b)))} />
                </Field>
                <MoneyInput id={`tariff-band-price-${i}`} label={t('tariff.bands.price')} currency={currency} locale={lang} valueMinor={row.priceMinor} onChangeMinor={(v) => set('bands')(form.bands.map((b, j) => (j === i ? { ...b, priceMinor: v } : b)))} issueText={moneyIssue} disabled={!currencyOk} />
              </FormRow>
            ))}
            <Cluster>
              <Button variant="secondary" onClick={() => set('bands')([...form.bands, emptyBandRow()])} disabled={form.bands.length >= MAX_TARIFF_BANDS}>{t('tariff.bands.add')}</Button>
            </Cluster>
            </Stack>

            <Stack gap={3}>
            <Stack gap={1}>
              <SectionTitle level={3} size="sub">{t('tariff.pairs.title')}</SectionTitle>
              <Text as="p" role="caption" tone={issueOf('pairs') !== undefined ? 'danger' : 'secondary'}>{issueOf('pairs') ?? t('tariff.pairs.hint')}</Text>
            </Stack>
            {form.pairs.map((row, i) => (
              <FormRow key={`pair-${i}`} action={<Button variant="ghost" onClick={() => set('pairs')(form.pairs.filter((_, j) => j !== i))}>{t('tariff.row.remove')}</Button>}>
                <Field label={t('tariff.pairs.destination')} htmlFor={`tariff-pair-dest-${i}`}>
                  <Select id={`tariff-pair-dest-${i}`} value={row.destinationMarketId} onChange={(e) => set('pairs')(form.pairs.map((r, j) => (j === i ? { ...r, destinationMarketId: e.target.value } : r)))}>
                    <option value="">{t('tariff.filter.market.pick')}</option>
                    {options.filter((m) => m.id !== loaded.marketId).map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </Select>
                </Field>
                <MoneyInput id={`tariff-pair-fixed-${i}`} label={t('tariff.pairs.fixed')} currency={currency} locale={lang} valueMinor={row.fixedMinor} onChangeMinor={(v) => set('pairs')(form.pairs.map((r, j) => (j === i ? { ...r, fixedMinor: v } : r)))} issueText={moneyIssue} disabled={!currencyOk} />
                <MoneyInput id={`tariff-pair-additional-${i}`} label={t('tariff.pairs.additional')} currency={currency} locale={lang} valueMinor={row.additionalMinor} onChangeMinor={(v) => set('pairs')(form.pairs.map((r, j) => (j === i ? { ...r, additionalMinor: v } : r)))} issueText={moneyIssue} disabled={!currencyOk} />
              </FormRow>
            ))}
            <Cluster>
              <Button variant="secondary" onClick={() => set('pairs')([...form.pairs, emptyPairRow()])} disabled={form.pairs.length >= MAX_TARIFF_PAIRS}>{t('tariff.pairs.add')}</Button>
            </Cluster>
            </Stack>

            <Field label={t('tariff.field.reason')} htmlFor="tariff-reason" {...(issueOf('reason') !== undefined ? { hint: issueOf('reason') } : {})}>
              <TextInput id="tariff-reason" value={form.reason} placeholder={t('tariff.field.reason.ph')} onChange={(e) => set('reason')(e.target.value)} aria-invalid={issueOf('reason') !== undefined} />
            </Field>
            <Cluster justify="end">
              <Button variant="primary" onClick={() => void submit()} disabled={busy || tz === undefined}>{busy ? t('tariff.submit.busy') : t('tariff.submit')}</Button>
            </Cluster>
            </Stack>
          </Panel>
        </>
      )}
    </>
  );
}
